/**
 * Signs someone out after a spell of inactivity, warning them first.
 *
 * WHAT THIS PROTECTS
 * ------------------
 * An unattended, unlocked laptop. Reps use this app in bars, restaurants
 * and customer back-offices; a screen left open on the territory list is
 * the realistic exposure, and this closes it.
 *
 * WHAT IT DOES NOT PROTECT
 * ------------------------
 * The token itself. A JWT stays valid server-side for its full lifetime
 * whatever the browser does — there is no revocation — so signing out
 * here only removes it from THIS machine. Someone who already copied it
 * keeps whatever time is left on the clock. Shortening the server TTL is
 * the lever for that; this is the lever for the laptop.
 *
 * ── How idleness is measured ──────────────────────────────────────────
 * Real intent only: pointer, key, wheel, touch. Deliberately NOT
 * `mousemove` — a nudged desk or a cat keeps a session alive forever,
 * which is precisely the case this exists to catch.
 *
 * The last-activity stamp lives in `localStorage`, so every tab shares
 * one clock. Working in one tab keeps the others alive; without that,
 * a second tab left open on a dashboard would count as idle and sign
 * you out mid-sentence in the first.
 *
 * The timer is a wall-clock comparison, not a countdown, so it survives
 * a laptop being closed: sleep for an hour and you come back already
 * past the deadline rather than with 20 minutes still queued up.
 *
 * ── Background tabs ───────────────────────────────────────────────────
 * The clock keeps running when this tab is not the one in front. That is
 * deliberate and matches how business platforms treat an idle session —
 * being busy in another tab is not a reason to hold a privileged session
 * open, and pausing for hidden tabs would let a forgotten one live
 * indefinitely.
 *
 * It does mean the warning can be spent where nobody sees it. The answer
 * is not to weaken the clock but to explain the outcome: sign-out
 * carries `reason=idle`, and the sign-in screen says what happened.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** How long without real activity before we ask. */
const IDLE_MS = 20 * 60_000;

/** How long the warning stays up before we act on it. */
export const WARN_MS = 60_000;

/** Shared across tabs, so one clock governs the whole browser. */
const ACTIVITY_KEY = 'hy.auth.lastActivity';

/**
 * Writing on every keystroke would hammer localStorage, which is
 * synchronous and shared. Once every few seconds is plenty for a
 * twenty-minute threshold.
 */
const WRITE_THROTTLE_MS = 5_000;

/** Intent, not presence. See the note above about `mousemove`. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

function readLastActivity(): number {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : Date.now();
  } catch {
    // Private mode / storage disabled. Treat now as the last activity so
    // a storage failure can never sign somebody out.
    return Date.now();
  }
}

function writeLastActivity(at: number): void {
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    // See readLastActivity.
  }
}

interface IdleState {
  /** True once the warning window has opened. */
  warning: boolean;
  /** Whole seconds left before sign-out. Only meaningful while warning. */
  secondsLeft: number;
  /** "I'm still here" — resets the clock everywhere. */
  stayActive: () => void;
}

/**
 * @param enabled  Only run for a signed-in person on a real page.
 * @param onExpire Called once when the warning runs out.
 */
export function useIdleTimeout(enabled: boolean, onExpire: () => void): IdleState {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(WARN_MS / 1000));

  const lastWrite = useRef(0);
  // Latest callback without re-subscribing the interval every render.
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;
  // One expiry per idle episode, however many ticks land after zero.
  const firedRef = useRef(false);
  // Mirrors `warning` so the activity listener can read it without being
  // torn down and re-registered every time the countdown ticks.
  const warningRef = useRef(false);
  warningRef.current = warning;

  const stayActive = useCallback(() => {
    const now = Date.now();
    writeLastActivity(now);
    lastWrite.current = now;
    firedRef.current = false;
    setWarning(false);
    setSecondsLeft(Math.ceil(WARN_MS / 1000));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setWarning(false);
      return;
    }

    // Landing here counts as activity; without this a fresh sign-in
    // would inherit a stale stamp from an earlier session in this
    // browser and could warn immediately.
    stayActive();

    const onActivity = () => {
      const now = Date.now();
      // While the warning is up, ordinary activity must NOT dismiss it.
      // Someone reaching for the mouse to read the dialog would cancel
      // the very thing they are being asked to answer, and would never
      // learn the session was about to end.
      if (firedRef.current || warningRef.current) return;
      if (now - lastWrite.current < WRITE_THROTTLE_MS) return;
      writeLastActivity(now);
      lastWrite.current = now;
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    const tick = () => {
      const idleFor = Date.now() - readLastActivity();
      if (idleFor >= IDLE_MS + WARN_MS) {
        if (!firedRef.current) {
          firedRef.current = true;
          setWarning(false);
          expireRef.current();
        }
        return;
      }
      if (idleFor >= IDLE_MS) {
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_MS + WARN_MS - idleFor) / 1000)));
        return;
      }
      setWarning(false);
    };

    const timer = window.setInterval(tick, 1_000);
    tick();

    return () => {
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity);
      window.clearInterval(timer);
    };
  }, [enabled, stayActive]);

  return { warning, secondsLeft, stayActive };
}
